import { FaCalculator, FaSearch, FaSpinner } from 'react-icons/fa';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';


const Api4Form = ({ state, updateState }) => {
    const formData = state.formData || {};
    const CLIMATIQ_API_KEY = process.env.REACT_APP_CLIMATIQ_API_KEY || 'Y2ZBNWZKRzRGNzJYNjRQMzpa';

    const fuelSearchConfig = {
        diesel: {
            query: 'diesel fuel combustion mobile',
            preferred_unit_type: 'volume'
        },
        gasoline: {
            query: 'gasoline vehicle',
            preferred_unit_type: 'volume'
        }
    };

    const conversionRates = {
        diesel: 1.2, gasoline: 1.1
    };

    // Search for quantity-based emission factors
    const searchQuantityEmissionFactors = async (fuelType) => {
        try {
            const config = fuelSearchConfig[fuelType];

            // Try specific activity IDs first for diesel
            if (fuelType === 'diesel') {
                const specificActivityIds = [
                    'diesel_fuel_combustion',
                    'mobile_combustion_diesel',
                    'passenger_vehicle_diesel',
                    'commercial_vehicle_diesel'
                ];

                for (const activityId of specificActivityIds) {
                    const params = new URLSearchParams({
                        query: activityId,
                        data_version: '^0',
                        region: formData.region || 'US',
                        year: formData.year || new Date().getFullYear().toString(),
                        results_per_page: '5'
                    });

                    const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                            'Content-Type': 'application/json',
                        },
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.results && data.results.length > 0) {
                            const bestFactor = data.results[0];
                            console.log(`Found diesel factor via specific search:`, bestFactor);
                            return bestFactor;
                        }
                    }
                }
            }

            // Fallback to general search for all fuel types
            const params = new URLSearchParams({
                query: config.query,
                data_version: '^0',
                region: formData.region || 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '10'
            });

            console.log(`Searching for ${fuelType} with query: ${config.query}`);

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error(`Search API failed with status ${response.status}`);
            const data = await response.json();

            console.log(`Raw API results for ${fuelType}:`, data.results);

            if (data.results && data.results.length > 0) {
                // More flexible filtering
                const volumeFactors = data.results.filter(factor => {
                    // For diesel, be more permissive with the filters
                    if (fuelType === 'diesel') {
                        return (
                            (factor.activity_id.includes('diesel') ||
                                factor.name.toLowerCase().includes('diesel') ||
                                factor.description?.toLowerCase().includes('diesel')) &&
                            (factor.unit.includes('liter') ||
                                factor.unit.includes('litre') ||
                                factor.unit.includes('l') ||
                                factor.unit_type === 'volume' ||
                                factor.unit_type === 'energy')
                        );
                    }

                    // For gasoline, use original filters
                    return (
                        (factor.activity_id.includes('vehicle') ||
                            factor.activity_id.includes('mobile') ||
                            factor.category === 'Vehicles' ||
                            factor.name.toLowerCase().includes('gasoline')) &&
                        (factor.unit.includes('liter') ||
                            factor.unit.includes('litre') ||
                            factor.unit_type === 'volume')
                    );
                });

                console.log(`Filtered volume factors for ${fuelType}:`, volumeFactors);

                let bestFactor = volumeFactors[0] || data.results[0];

                if (config.preferred_unit_type && volumeFactors.length > 0) {
                    const preferredFactor = volumeFactors.find(f => f.unit_type === config.preferred_unit_type);
                    if (preferredFactor) bestFactor = preferredFactor;
                }

                // If no factors found, use the first available result
                if (!bestFactor && data.results.length > 0) {
                    console.log(`No specific factors found for ${fuelType}, using first available result`);
                    bestFactor = data.results[0];
                }

                if (bestFactor) {
                    console.log(`Selected factor for ${fuelType}:`, bestFactor);
                    return bestFactor;
                }
            }

            console.log(`No results found for ${fuelType}`);
            return null;
        } catch (error) {
            console.error(`Error searching emission factors for ${fuelType}:`, error);
            return null;
        }
    };

    // Search for spend-based emission factors
    const searchSpendEmissionFactors = async (fuelType) => {
        try {
            const config = fuelSearchConfig[fuelType];
            const params = new URLSearchParams({
                query: `${config.query} spend money usd`,
                data_version: '^0',
                region: formData.region || 'US',
                year: formData.year || new Date().getFullYear().toString(),
                results_per_page: '5'
            });

            console.log(`Searching spend factors for ${fuelType} with query: ${config.query} spend money usd`);

            const response = await fetch(`https://api.climatiq.io/data/v1/search?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error(`Search API failed with status ${response.status}`);
            const data = await response.json();

            console.log(`Raw spend API results for ${fuelType}:`, data.results);

            if (data.results && data.results.length > 0) {
                const spendFactors = data.results.filter(factor =>
                    factor.unit_type === 'money' ||
                    factor.unit.includes('usd') ||
                    factor.unit.includes('USD') ||
                    factor.unit.includes('money')
                );

                let bestFactor = spendFactors[0] || data.results[0];

                console.log(`Selected spend factor for ${fuelType}:`, bestFactor);
                return bestFactor;
            }

            console.log(`No spend results found for ${fuelType}`);
            return null;
        } catch (error) {
            console.error(`Error searching spend factors for ${fuelType}:`, error);
            return null;
        }
    };

    const handleSearchFactors = async () => {
        const selectedFuels = [];
        if (formData.diesel?.selected) selectedFuels.push('diesel');
        if (formData.gasoline?.selected) selectedFuels.push('gasoline');

        if (selectedFuels.length === 0) {
            toastr.error('Please select at least one fuel type');
            return;
        }

        updateState({ isSearching: true });
        toastr.info('Searching for best emission factors...');

        try {
            let foundCount = 0;
            const newAvailableFactors = { ...state.availableFactors };
            const newSpendFactors = { ...state.spendFactors };

            // Use Promise.all to search for all fuels simultaneously
            const searchPromises = selectedFuels.map(async (fuelType) => {
                console.log(`Searching factors for: ${fuelType}`);

                const [quantityFactor, spendFactor] = await Promise.all([
                    searchQuantityEmissionFactors(fuelType),
                    searchSpendEmissionFactors(fuelType)
                ]);

                if (quantityFactor) {
                    newAvailableFactors[fuelType] = quantityFactor;
                    console.log(`Found quantity factor for ${fuelType}:`, quantityFactor.activity_id);
                    foundCount++;
                } else {
                    console.log(`No quantity factor found for ${fuelType}`);
                }

                if (spendFactor) {
                    newSpendFactors[fuelType] = spendFactor;
                    console.log(`Found spend factor for ${fuelType}:`, spendFactor.activity_id);
                    foundCount++;
                } else {
                    console.log(`No spend factor found for ${fuelType}`);
                }

                return { fuelType, quantityFactor, spendFactor };
            });

            // Wait for all searches to complete
            await Promise.all(searchPromises);

            // Update state once with all results
            updateState({
                availableFactors: newAvailableFactors,
                spendFactors: newSpendFactors
            });

            if (foundCount > 0) {
                toastr.success(`Found emission factors for ${foundCount} fuel(s)! Click Calculate to proceed.`);
            } else {
                toastr.warning('No specific factors found. Please try different search parameters.');
            }
        } catch (error) {
            console.error('Search error:', error);
            toastr.error('Search failed. Please check your API key and try again.');
        } finally {
            updateState({ isSearching: false });
        }
    };

    // Rest of the code remains the same...
    // [Keep all the other functions exactly as they were - handleFuelToggle, handleFuelDataChange, callQuantityClimateIQAPI, callSpendClimateIQAPI, handleCalculate, etc.]

    // Quantity-based API call
    const callQuantityClimateIQAPI = async (fuelType, quantity, emissionFactor) => {
        try {
            let requestBody;

            // Use the found emission factor if available
            if (emissionFactor) {
                requestBody = {
                    emission_factor: {
                        activity_id: emissionFactor.activity_id,
                        source: emissionFactor.source,
                        region: emissionFactor.region,
                        year: parseInt(emissionFactor.year),
                        data_version: "^0"
                    },
                    parameters: {
                        volume: quantity,
                        volume_unit: "l"
                    }
                };
            } else {
                // Fallback to hardcoded activity IDs
                if (fuelType === 'diesel') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "fuel-type_diesel-fuel_use_mobile_locomotives",
                            source: "EPA",
                            region: formData.region || 'US',
                            year: parseInt(formData.year || new Date().getFullYear().toString()),
                            source_lca_activity: "fuel_combustion",
                            data_version: "^0"
                        },
                        parameters: {
                            volume: quantity,
                            volume_unit: "l"
                        }
                    };
                } else if (fuelType === 'gasoline') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "fuel-type_aviation_gasoline-fuel_use_mobile_combustion_aviation",
                            data_version: "^0"
                        },
                        parameters: {
                            volume: quantity,
                            volume_unit: "l"
                        }
                    };
                }
            }

            console.log(`Calling quantity API for ${fuelType}:`, requestBody);

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${fuelType}:`, error);
            throw error;
        }
    };

    // Spend-based API call
    const callSpendClimateIQAPI = async (fuelType, spend, emissionFactor) => {
        try {
            let requestBody;

            // Use the found emission factor if available
            if (emissionFactor) {
                requestBody = {
                    emission_factor: {
                        activity_id: emissionFactor.activity_id,
                        source: emissionFactor.source,
                        region: emissionFactor.region,
                        year: parseInt(emissionFactor.year),
                        data_version: "^0"
                    },
                    parameters: {
                        money: spend,
                        money_unit: "usd"
                    }
                };
            } else {
                // Fallback to generic spend factors
                if (fuelType === 'diesel') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "fuel-type_diesel-fuel_spend",
                            source: "EPA",
                            region: formData.region || 'US',
                            year: parseInt(formData.year || new Date().getFullYear().toString()),
                            data_version: "^0"
                        },
                        parameters: {
                            money: spend,
                            money_unit: "usd"
                        }
                    };
                } else if (fuelType === 'gasoline') {
                    requestBody = {
                        emission_factor: {
                            activity_id: "fuel-type_gasoline_spend",
                            source: "EPA",
                            region: formData.region || 'US',
                            year: parseInt(formData.year || new Date().getFullYear().toString()),
                            data_version: "^0"
                        },
                        parameters: {
                            money: spend,
                            money_unit: "usd"
                        }
                    };
                }
            }

            console.log(`Calling spend API for ${fuelType}:`, requestBody);

            const response = await fetch('https://api.climatiq.io/data/v1/estimate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${CLIMATIQ_API_KEY}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`API request failed with status ${response.status}: ${errorData}`);
            }

            return await response.json();
        } catch (error) {
            console.error(`Error calling ClimateIQ API for ${fuelType}:`, error);
            throw error;
        }
    };

    const handleFuelToggle = (fuelType) => {
        const fuelData = formData[fuelType] || {
            selected: false,
            quantity: '',
            annualSpend: '',
            pricePerLiter: '',
            inputMethod: 'quantity'
        };
        updateState({
            formData: {
                ...formData,
                [fuelType]: {
                    ...fuelData,
                    selected: !fuelData.selected,
                    inputMethod: 'quantity'
                }
            }
        });
    };

    const handleFuelDataChange = (fuelType, field, value) => {
        const fuelData = formData[fuelType] || {
            selected: false,
            quantity: '',
            annualSpend: '',
            pricePerLiter: '',
            inputMethod: 'quantity'
        };

        if (field === 'inputMethod') {
            updateState({
                formData: {
                    ...formData,
                    [fuelType]: {
                        ...fuelData,
                        inputMethod: value,
                        quantity: value === 'quantity' ? fuelData.quantity : '',
                        annualSpend: value === 'spend' ? fuelData.annualSpend : ''
                    }
                }
            });
        } else {
            updateState({
                formData: {
                    ...formData,
                    [fuelType]: {
                        ...fuelData,
                        [field]: value
                    }
                }
            });
        }
    };

    const handleCalculate = async () => {
        const selectedFuels = [];
        if (formData.diesel?.selected) selectedFuels.push('diesel');
        if (formData.gasoline?.selected) selectedFuels.push('gasoline');

        if (selectedFuels.length === 0) {
            toastr.error('Please select at least one fuel type');
            return;
        }

        for (const fuelType of selectedFuels) {
            const data = formData[fuelType];

            if (data.inputMethod === 'quantity' && (!data.quantity || isNaN(parseFloat(data.quantity)))) {
                toastr.error(`Please provide quantity for ${fuelType}`);
                return;
            }

            if (data.inputMethod === 'spend' && (!data.annualSpend || isNaN(parseFloat(data.annualSpend)))) {
                toastr.error(`Please provide annual spend for ${fuelType}`);
                return;
            }
        }

        if (Object.keys(state.availableFactors || {}).length === 0 && Object.keys(state.spendFactors || {}).length === 0) {
            toastr.error('Please search for emission factors first');
            return;
        }

        updateState({ isCalculating: true, calculationResults: {} });
        toastr.info('Calculating emissions via API...');

        try {
            const results = {};
            let hasSuccessfulCalculation = false;

            for (const fuelType of selectedFuels) {
                const data = formData[fuelType];

                try {
                    let apiResult;

                    if (data.inputMethod === 'quantity') {
                        const quantity = parseFloat(data.quantity);
                        const emissionFactor = state.availableFactors[fuelType];

                        if (!emissionFactor) {
                            toastr.warning(`No quantity emission factor found for ${fuelType}`);
                            continue;
                        }

                        apiResult = await callQuantityClimateIQAPI(fuelType, quantity, emissionFactor);

                        results[fuelType] = {
                            quantity,
                            unit: 'L',
                            co2e: apiResult.co2e,
                            co2e_unit: apiResult.co2e_unit,
                            calculation_method: apiResult.co2e_calculation_method,
                            activity_data: apiResult.activity_data,
                            emission_factor: emissionFactor,
                            inputMethod: 'quantity'
                        };

                    } else {
                        const spend = parseFloat(data.annualSpend);
                        const emissionFactor = state.spendFactors[fuelType];

                        if (!emissionFactor) {
                            toastr.warning(`No spend emission factor found for ${fuelType}`);
                            continue;
                        }

                        apiResult = await callSpendClimateIQAPI(fuelType, spend, emissionFactor);

                        results[fuelType] = {
                            spend,
                            currency: 'USD',
                            co2e: apiResult.co2e,
                            co2e_unit: apiResult.co2e_unit,
                            calculation_method: apiResult.co2e_calculation_method,
                            activity_data: apiResult.activity_data,
                            emission_factor: emissionFactor,
                            inputMethod: 'spend'
                        };
                    }

                    toastr.success(`Calculated emissions for ${fuelType}: ${apiResult.co2e.toFixed(2)} ${apiResult.co2e_unit}`);
                    hasSuccessfulCalculation = true;

                } catch (error) {
                    console.error(`API error for ${fuelType}:`, error);
                    toastr.error(`Failed to calculate emissions for ${fuelType}: ${error.message}`);
                }
            }

            updateState({ calculationResults: results });

            if (hasSuccessfulCalculation) {
                const totalEmissions = Object.values(results).reduce((sum, result) => sum + (result.co2e || 0), 0);
                toastr.success(`Calculation complete! Total emissions: ${totalEmissions.toFixed(2)} kg CO2e`);
            } else {
                toastr.warning('No emissions could be calculated. Please check your inputs and API key.');
            }
        } catch (error) {
            toastr.error('Calculation failed. Please check your inputs and try again.');
            console.error('Calculation error:', error);
        } finally {
            updateState({ isCalculating: false });
        }
    };

    const loadSampleData = () => {
        updateState({
            formData: {
                ...formData,
                year: '2024',
                diesel: {
                    selected: true,
                    quantity: '',
                    annualSpend: '12000',
                    pricePerLiter: '',
                    inputMethod: 'spend'
                },
                gasoline: {
                    selected: true,
                    quantity: '2000',
                    annualSpend: '',
                    pricePerLiter: '',
                    inputMethod: 'quantity'
                }
            }
        });
        toastr.info('Sample data loaded. Click "Search Factors" to find emission factors.');
    };

    // [Keep the JSX return statement exactly the same as before]
    return (
        <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                <div className="p-8">
                    <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            <strong>API Status:</strong> {CLIMATIQ_API_KEY && CLIMATIQ_API_KEY !== 'Y2ZBNWZKRzRGNzJYNjRQMzpa' ?
                                '✅ API Key Configured' : '❌ Please set REACT_APP_CLIMATIQ_API_KEY in .env file'}
                        </p>
                    </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={loadSampleData}
                            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 text-sm"
                        >
                            Load Sample Data
                        </button>
                    </div>

                    <div className="mb-8 grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                            <select
                                value={formData.year || new Date().getFullYear().toString()}
                                onChange={(e) => updateState({ formData: { ...formData, year: e.target.value } })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {Array.from({ length: new Date().getFullYear() - 2009 }, (_, i) =>
                                    (new Date().getFullYear() - i).toString()
                                ).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Region</label>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={formData.region || 'US'}
                                    disabled
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                                />
                                <span className="text-sm text-gray-500">(United States)</span>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6">
                        <button
                            onClick={handleSearchFactors}
                            disabled={state.isSearching}
                            className="px-6 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isSearching ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaSearch className="w-4 h-4" />
                            )}
                            <span>{state.isSearching ? 'Searching...' : 'Search Emission Factors'}</span>
                        </button>
                    </div>

                    {(Object.keys(state.availableFactors || {}).length > 0 || Object.keys(state.spendFactors || {}).length > 0) && (
                        <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
                            <h3 className="font-semibold text-green-800 mb-2">Available Emission Factors</h3>
                            <div className="space-y-2">
                                {Object.entries(state.availableFactors || {}).map(([fuelType, factor]) => (
                                    <div key={fuelType} className="text-sm text-green-700">
                                        <strong>{fuelType} (Quantity):</strong> {factor.name} ({factor.source}, {factor.region})
                                        <br />
                                        <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                    </div>
                                ))}
                                {Object.entries(state.spendFactors || {}).map(([fuelType, factor]) => (
                                    <div key={fuelType} className="text-sm text-green-700">
                                        <strong>{fuelType} (Spend):</strong> {factor.name} ({factor.source}, {factor.region})
                                        <br />
                                        <span className="text-xs">Unit: {factor.unit} | Type: {factor.unit_type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <label className="block text-lg font-semibold text-gray-700 mb-4">
                            How many liters of mobile fuel does the company consume per year?
                        </label>
                        <p className="text-sm text-gray-600 mb-4">
                            This information can often be found in fleet cards, telematics and/or fuel logs.
                            Please provide your answer(s) in liters of diesel and/or gasoline per year.
                            Alternatively, EcoMina can calculate your mobile fuel consumption based on how much you spend in US dollars, per year.
                        </p>

                        <div className="mb-8 space-y-6">
                            {/* Diesel Option */}
                            <div className={`border-2 rounded-lg p-6 transition-all ${formData.diesel?.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                                }`}>
                                <div className="flex items-start space-x-3 mb-4">
                                    <input
                                        type="checkbox"
                                        checked={formData.diesel?.selected || false}
                                        onChange={() => handleFuelToggle('diesel')}
                                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-700 cursor-pointer">Diesel</label>
                                        <p className="text-sm text-gray-600 mt-1">Company vehicles, trucks, heavy equipment</p>

                                        {state.availableFactors?.diesel && (
                                            <div className="mt-2 p-2 bg-blue-100 rounded-lg">
                                                <p className="text-xs text-blue-800">
                                                    <strong>Quantity Emission Factor:</strong> {state.availableFactors.diesel.name} ({state.availableFactors.diesel.source}, {state.availableFactors.diesel.region})
                                                    <br />
                                                    <strong>Unit:</strong> {state.availableFactors.diesel.unit} | <strong>Type:</strong> {state.availableFactors.diesel.unit_type}
                                                </p>
                                            </div>
                                        )}

                                        {state.spendFactors?.diesel && (
                                            <div className="mt-2 p-2 bg-purple-100 rounded-lg">
                                                <p className="text-xs text-purple-800">
                                                    <strong>Spend Emission Factor:</strong> {state.spendFactors.diesel.name} ({state.spendFactors.diesel.source}, {state.spendFactors.diesel.region})
                                                    <br />
                                                    <strong>Unit:</strong> {state.spendFactors.diesel.unit} | <strong>Type:</strong> {state.spendFactors.diesel.unit_type}
                                                </p>
                                            </div>
                                        )}

                                        {state.calculationResults?.diesel && (
                                            <div className="mt-2 p-2 bg-green-100 rounded-lg">
                                                <p className="text-sm text-green-800 font-medium">
                                                    Emissions: {state.calculationResults.diesel.co2e.toFixed(2)} {state.calculationResults.diesel.co2e_unit}
                                                </p>
                                                <p className="text-xs text-green-600">
                                                    {state.calculationResults.diesel.inputMethod === 'quantity'
                                                        ? `Based on ${state.calculationResults.diesel.quantity} ${state.calculationResults.diesel.unit}`
                                                        : `Based on $${state.calculationResults.diesel.spend} USD`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {formData.diesel?.selected && (
                                    <div className="ml-8 space-y-4">
                                        <div className="flex space-x-4 mb-4">
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="diesel-method"
                                                    value="quantity"
                                                    checked={formData.diesel.inputMethod === 'quantity'}
                                                    onChange={(e) => handleFuelDataChange('diesel', 'inputMethod', e.target.value)}
                                                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                />
                                                <span className="ml-2 text-sm font-medium text-gray-700">Quantity Based</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="diesel-method"
                                                    value="spend"
                                                    checked={formData.diesel.inputMethod === 'spend'}
                                                    onChange={(e) => handleFuelDataChange('diesel', 'inputMethod', e.target.value)}
                                                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                />
                                                <span className="ml-2 text-sm font-medium text-gray-700">Spend Based</span>
                                            </label>
                                        </div>

                                        {formData.diesel.inputMethod === 'quantity' ? (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Quantity (liters of diesel per year) *
                                                </label>
                                                <div className="flex items-center space-x-2 max-w-md">
                                                    <input
                                                        type="number"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter liters"
                                                        value={formData.diesel.quantity || ''}
                                                        onChange={(e) => handleFuelDataChange('diesel', 'quantity', e.target.value)}
                                                    />
                                                    <span className="text-gray-600 text-sm font-medium">L</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Annual Spend (US Dollars) *
                                                </label>
                                                <div className="flex items-center space-x-2 max-w-md">
                                                    <span className="text-gray-600 font-medium text-lg">$</span>
                                                    <input
                                                        type="number"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter amount"
                                                        value={formData.diesel.annualSpend || ''}
                                                        onChange={(e) => handleFuelDataChange('diesel', 'annualSpend', e.target.value)}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    EcoMina will use spend-based emission factors directly
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Gasoline Option */}
                            <div className={`border-2 rounded-lg p-6 transition-all ${formData.gasoline?.selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                                }`}>
                                <div className="flex items-start space-x-3 mb-4">
                                    <input
                                        type="checkbox"
                                        checked={formData.gasoline?.selected || false}
                                        onChange={() => handleFuelToggle('gasoline')}
                                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-1"
                                    />
                                    <div className="flex-1">
                                        <label className="font-semibold text-gray-700 cursor-pointer">Gasoline</label>
                                        <p className="text-sm text-gray-600 mt-1">Cars, light trucks, small equipment</p>

                                        {state.availableFactors?.gasoline && (
                                            <div className="mt-2 p-2 bg-blue-100 rounded-lg">
                                                <p className="text-xs text-blue-800">
                                                    <strong>Quantity Emission Factor:</strong> {state.availableFactors.gasoline.name} ({state.availableFactors.gasoline.source}, {state.availableFactors.gasoline.region})
                                                    <br />
                                                    <strong>Unit:</strong> {state.availableFactors.gasoline.unit} | <strong>Type:</strong> {state.availableFactors.gasoline.unit_type}
                                                </p>
                                            </div>
                                        )}

                                        {state.spendFactors?.gasoline && (
                                            <div className="mt-2 p-2 bg-purple-100 rounded-lg">
                                                <p className="text-xs text-purple-800">
                                                    <strong>Spend Emission Factor:</strong> {state.spendFactors.gasoline.name} ({state.spendFactors.gasoline.source}, {state.spendFactors.gasoline.region})
                                                    <br />
                                                    <strong>Unit:</strong> {state.spendFactors.gasoline.unit} | <strong>Type:</strong> {state.spendFactors.gasoline.unit_type}
                                                </p>
                                            </div>
                                        )}

                                        {state.calculationResults?.gasoline && (
                                            <div className="mt-2 p-2 bg-green-100 rounded-lg">
                                                <p className="text-sm text-green-800 font-medium">
                                                    Emissions: {state.calculationResults.gasoline.co2e.toFixed(2)} {state.calculationResults.gasoline.co2e_unit}
                                                </p>
                                                <p className="text-xs text-green-600">
                                                    {state.calculationResults.gasoline.inputMethod === 'quantity'
                                                        ? `Based on ${state.calculationResults.gasoline.quantity} ${state.calculationResults.gasoline.unit}`
                                                        : `Based on $${state.calculationResults.gasoline.spend} USD`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {formData.gasoline?.selected && (
                                    <div className="ml-8 space-y-4">
                                        <div className="flex space-x-4 mb-4">
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="gasoline-method"
                                                    value="quantity"
                                                    checked={formData.gasoline.inputMethod === 'quantity'}
                                                    onChange={(e) => handleFuelDataChange('gasoline', 'inputMethod', e.target.value)}
                                                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                />
                                                <span className="ml-2 text-sm font-medium text-gray-700">Quantity Based</span>
                                            </label>
                                            <label className="flex items-center">
                                                <input
                                                    type="radio"
                                                    name="gasoline-method"
                                                    value="spend"
                                                    checked={formData.gasoline.inputMethod === 'spend'}
                                                    onChange={(e) => handleFuelDataChange('gasoline', 'inputMethod', e.target.value)}
                                                    className="w-4 h-4 text-green-600 border-gray-300 focus:ring-green-500"
                                                />
                                                <span className="ml-2 text-sm font-medium text-gray-700">Spend Based</span>
                                            </label>
                                        </div>

                                        {formData.gasoline.inputMethod === 'quantity' ? (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Quantity (liters of gasoline per year) *
                                                </label>
                                                <div className="flex items-center space-x-2 max-w-md">
                                                    <input
                                                        type="number"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter liters"
                                                        value={formData.gasoline.quantity || ''}
                                                        onChange={(e) => handleFuelDataChange('gasoline', 'quantity', e.target.value)}
                                                    />
                                                    <span className="text-gray-600 text-sm font-medium">L</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Annual Spend (US Dollars) *
                                                </label>
                                                <div className="flex items-center space-x-2 max-w-md">
                                                    <span className="text-gray-600 font-medium text-lg">$</span>
                                                    <input
                                                        type="number"
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                                                        placeholder="Enter amount"
                                                        value={formData.gasoline.annualSpend || ''}
                                                        onChange={(e) => handleFuelDataChange('gasoline', 'annualSpend', e.target.value)}
                                                    />
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    EcoMina will use spend-based emission factors directly
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {Object.keys(state.calculationResults || {}).length > 0 && (
                            <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Results</h3>
                                <div className="space-y-2">
                                    {state.calculationResults.diesel && (
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <span className="text-gray-700">Diesel</span>
                                                <span className="text-xs text-gray-500 ml-2">
                                                    ({state.calculationResults.diesel.inputMethod === 'quantity'
                                                        ? `${state.calculationResults.diesel.quantity} ${state.calculationResults.diesel.unit}`
                                                        : `$${state.calculationResults.diesel.spend} USD`})
                                                </span>
                                            </div>
                                            <span className="font-semibold text-green-700">
                                                {state.calculationResults.diesel.co2e.toFixed(2)} {state.calculationResults.diesel.co2e_unit}
                                            </span>
                                        </div>
                                    )}
                                    {state.calculationResults.gasoline && (
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <span className="text-gray-700">Gasoline</span>
                                                <span className="text-xs text-gray-500 ml-2">
                                                    ({state.calculationResults.gasoline.inputMethod === 'quantity'
                                                        ? `${state.calculationResults.gasoline.quantity} ${state.calculationResults.gasoline.unit}`
                                                        : `$${state.calculationResults.gasoline.spend} USD`})
                                                </span>
                                            </div>
                                            <span className="font-semibold text-green-700">
                                                {state.calculationResults.gasoline.co2e.toFixed(2)} {state.calculationResults.gasoline.co2e_unit}
                                            </span>
                                        </div>
                                    )}
                                    <div className="border-t border-gray-300 pt-2 mt-2">
                                        <div className="flex justify-between items-center font-bold text-lg">
                                            <span>Total Emissions:</span>
                                            <span className="text-blue-700">
                                                {Object.values(state.calculationResults).reduce((sum, result) => sum + (result.co2e || 0), 0).toFixed(2)} kg CO2e
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <button
                            onClick={handleCalculate}
                            disabled={state.isCalculating || (Object.keys(state.availableFactors || {}).length === 0 && Object.keys(state.spendFactors || {}).length === 0)}
                            className="px-6 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {state.isCalculating ? (
                                <FaSpinner className="w-4 h-4 animate-spin" />
                            ) : (
                                <FaCalculator className="w-4 h-4" />
                            )}
                            <span>{state.isCalculating ? 'Calculating...' : 'Calculate'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


export default Api4Form;